import { ReactNode, useEffect, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';

import { useBrowsing } from 'hooks/use-browsing';
import { useRefreshUserOnMount } from 'hooks/use-refresh-user-on-mount';

import { Toast } from 'core/generic/components/general/toast';
import GoogleTagManager from 'core/generic/helpers/google-tag-manager';

import {
	HeaderNavigation,
	HeaderNavigationData,
} from '../components/headers/main-navigation';
import { Footer, FooterData } from './footer';
import { Partners, PartnersData } from './partners';

type ManCityTemplateProps = {
	children: ReactNode;
	footerData?: FooterData;
	headerNavigationData?: HeaderNavigationData;
	partnersData?: PartnersData;
	hideTemplateRoutes?: string[];
	config?: MancityTemplateConfig;
	isSimpleHeader?: boolean;
	headerOnly?: boolean;
	stickyHeader?: boolean;
	showBasketIcon?: string;
	refreshUserOnMount?: boolean;
};

type MancityTemplateConfig = {
	gtmId?: string;
};

export const ManCityTemplate = ({
	children,
	footerData,
	headerNavigationData,
	partnersData,
	hideTemplateRoutes,
	config,
	headerOnly = false,
	stickyHeader,
	showBasketIcon = 'false',
	refreshUserOnMount = true,
}: ManCityTemplateProps) => {
	useRefreshUserOnMount({ enabled: refreshUserOnMount });

	const { query, pathname } = useBrowsing();
	const isFromApp = query?.fromapp !== undefined ? true : false;
	const isHideRouter = hideTemplateRoutes?.includes(pathname);
	const headerNavigationRef = useRef<HTMLDivElement>(null);
	const [headerVisible, setHeaderVisible] = useState(false);

	useEffect(() => {
		const headerEl = headerNavigationRef.current;
		if (!headerEl) {
			return;
		}

		const observer = new IntersectionObserver(
			([e]) => {
				if (e.isIntersecting) {
					setHeaderVisible(true);
				} else {
					setHeaderVisible(false);
				}
			},
			{ threshold: [1] }
		);

		observer.observe(headerEl);

		return () => {
			observer.unobserve(headerEl);
			observer.disconnect();
		};
	}, []);

	return (
		<>
			<Toast headerVisible={headerVisible} />

			{!isFromApp && !isHideRouter ? (
				<HeaderNavigation
					menuItems={headerNavigationData || []}
					stickyHeader={stickyHeader}
					showBasketIcon={showBasketIcon}
					ref={headerNavigationRef}
				/>
			) : null}

			<Tooltip
				place="top"
				id="tooltip"
				className="tooltip body-xsmall"
				openEvents={{ mouseenter: true, click: true }}
				closeEvents={{ mouseleave: true, click: true }}
				render={({ activeAnchor }) => {
					const title =
						activeAnchor?.getAttribute('data-tooltip-title');
					const description = activeAnchor?.getAttribute(
						'data-tooltip-description'
					);

					if (title || description) {
						return (
							<div className="tooltip__content">
								{title && (
									<div className="tooltip__title body-xsmall bold">
										{title}
									</div>
								)}
								{description && (
									<div className="tooltip__description body-xsmall">
										{description}
									</div>
								)}
							</div>
						);
					}

					return null;
				}}
			/>

			{children}

			{!headerOnly && !isFromApp && !isHideRouter && (
				<>
					<Partners data={partnersData} />
					<Footer data={footerData} />
				</>
			)}

			{config?.gtmId && <GoogleTagManager gtmId={config.gtmId} />}
		</>
	);
};

export default ManCityTemplate;
